import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo, { SITE_URL, SITE_NAME } from '../components/Seo';

const API = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

// Escape HTML before we inject the mini-markdown substitutions. Post bodies are
// admin-authored, but escaping first means a stray angle bracket (or any future
// less-trusted content source) can never inject markup — only the **bold** and
// *italic* spans we add ourselves are rendered as HTML.
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/posts/${encodeURIComponent(slug)}`);
        if (!cancelled) setPost(res.ok ? (await res.json()).post : null);
      } catch (_) {
        if (!cancelled) setPost(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Article structured data — computed unconditionally to keep hook order stable.
  const articleJsonLd = useMemo(() => {
    if (!post) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      datePublished: post.date,
      articleSection: post.tag,
      url: `${SITE_URL}/blog/${slug}`,
      author: { '@type': 'Organization', name: SITE_NAME },
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/stratos-logo.png` },
      },
      mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
    };
  }, [post, slug]);

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '120px 24px 60px', color: '#64748b' }}>
        Loading…
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', padding: '120px 24px 60px' }}>
        <Seo title="Post not found" path={`/blog/${slug}`} noindex />
        <h2 style={{ fontFamily: 'Playfair Display, serif', color: '#0a1628' }}>Post not found</h2>
        <Link to="/blog" className="btn btn-aero">← Back to Blog</Link>
      </div>
    );
  }

  const paragraphs = (post.content || '').trim().split('\n\n');

  return (
    <>
      <Seo
        title={post.title}
        description={(post.excerpt || post.content || '').slice(0, 155)}
        path={`/blog/${slug}`}
        type="article"
        jsonLd={articleJsonLd}
      />
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow" style={{ color: '#3ba3e8', position: 'relative' }}>{post.tag}</p>
          <h1 className="page-hero-title" style={{ maxWidth: '700px', margin: '0 auto 16px' }}>{post.title}</h1>
          <p className="page-hero-sub">{post.date}{post.date && post.readTime ? ' · ' : ''}{post.readTime}</p>
        </div>
      </section>

      <section style={{ padding: '72px 0 96px' }}>
        <div className="container" style={{ maxWidth: '760px' }}>
          <div style={{ marginBottom: '32px' }}>
            <Link to="/blog" style={{ color: '#1a6bb5', fontSize: '0.875rem', fontWeight: 600 }}>← Back to Blog</Link>
          </div>
          <article className="article-body">
            {paragraphs.map((para, i) => {
              if (para.startsWith('**') && para.endsWith('**')) {
                return <h3 key={i} style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', color: '#0a1628', margin: '32px 0 12px' }}>{para.slice(2, -2)}</h3>;
              }
              // Escape first, then layer on the bold/italic spans we control.
              const rendered = escapeHtml(para)
                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                .replace(/\*([^*]+)\*/g, '<em>$1</em>');
              return <p key={i} style={{ fontSize: '1.02rem', color: '#334155', lineHeight: 1.85, marginBottom: '20px' }} dangerouslySetInnerHTML={{ __html: rendered }} />;
            })}
          </article>

          <div style={{ marginTop: '48px', padding: '28px', background: '#eff6ff', borderRadius: '16px', border: '1px solid rgba(26,107,181,0.15)' }}>
            <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', color: '#0a1628', marginBottom: '12px' }}>Ready to celebrate your child's achievements?</p>
            <p style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '20px' }}>Book a SKIES session and get your official Stratos Standard certificate.</p>
            <Link to="/book" className="btn btn-primary">Book a Session</Link>
          </div>
        </div>
      </section>
    </>
  );
}
